import {
  Box,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Spinner as ChakraSpinner,
  useDisclosure,
  PopoverFooter,
} from '@chakra-ui/react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PDFExport } from '@progress/kendo-react-pdf';
import { pdf } from '@progress/kendo-drawing';

import DownloadIcon from 'components/Icons/DownloadIcon';
import FigmaFileDownloadIcon from 'components/Icons/FigmaFileDownloadIcon';
import bloxBiService from 'services/bloxBiService';
import {
  getSelectedDashboardScenario,
  getTimeFilter,
  getDashboard,
  getCompareDashboardScenario,
  getReportingAnchorPeriod,
  getReportingWindow,
} from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import { resolveReportingPeriod } from 'utils/reportingPeriodUtils';

import Preview from './Preview';
import { PageInstruments } from './Instruments/proptypes';
import './pdf-styles.css'; // You'll need to create this file

/**
 * Without an embedded font kendo falls back to the standard PDF fonts, which
 * are WinAnsi-encoded and have no rupee sign — every currency value exported
 * as `„` instead of `₹`. The bundled Roboto/Comfortaa webfonts are latin
 * subsets and don't carry U+20B9 either (and kendo can't embed woff2 anyway),
 * so the export ships its own full-coverage TTF. The family name has to match
 * what pdf-styles.css sets on the exported markup.
 */
/**
 * kendo draws from a clone of the DOM, and a clone doesn't carry scrollLeft /
 * scrollTop — every scrolled table came out reset to its first rows and
 * columns no matter where the preview was scrolled. Re-expressing the offset
 * as a transform on the content survives the clone, so the clipped viewport
 * shows the window the user actually chose. Returns undo callbacks.
 */
const pinScrollOffsets = (root: HTMLElement): Array<() => void> => {
  const restores: Array<() => void> = [];

  root.querySelectorAll<HTMLElement>('.scroll, .table_scroll').forEach((viewport) => {
    const { scrollLeft, scrollTop, firstElementChild } = viewport;
    const content = firstElementChild as HTMLElement | null;
    if (!content || (scrollLeft === 0 && scrollTop === 0)) return;

    const previousTransform = content.style.transform;
    content.style.transform = `translate(${-scrollLeft}px, ${-scrollTop}px)`;
    restores.push(() => {
      content.style.transform = previousTransform;
    });

    // The frozen first column is sticky, which kendo can't draw, so it goes
    // static for the export. Shifting it back by the same amount keeps the row
    // labels against the left edge the way the preview shows them.
    if (scrollLeft > 0) {
      content.querySelectorAll<HTMLElement>('tr > *:first-child').forEach((cell) => {
        const previousCellTransform = cell.style.transform;
        cell.style.transform = `translateX(${scrollLeft}px)`;
        restores.push(() => {
          cell.style.transform = previousCellTransform;
        });
      });
    }
  });

  return restores;
};

/* Applied to <body> only while kendo is drawing the PDF; see onExport. */
const EXPORT_CLIP_CLASS = 'pdf-export-clipping';

const NOTO_REGULAR = `${process.env.PUBLIC_URL}/fonts/NotoSans-Regular.ttf`;
const NOTO_BOLD = `${process.env.PUBLIC_URL}/fonts/NotoSans-Bold.ttf`;

/*
 * Every family the exported markup can actually resolve to is mapped onto the
 * same face, rather than relying on pdf-styles.css winning the cascade on
 * every node. The Variance cells set fontFamily='regular_roboto' inline, so
 * whichever family ends up computed, kendo still embeds a font that has the
 * glyph.
 */
pdf.defineFont({
  'Noto Sans': NOTO_REGULAR,
  'Noto Sans|bold': NOTO_BOLD,
  'Noto Sans|500': NOTO_REGULAR,
  'Noto Sans|600': NOTO_BOLD,
  'Noto Sans|700': NOTO_BOLD,
  regular_roboto: NOTO_REGULAR,
  'regular_roboto|bold': NOTO_BOLD,
  'Roboto Regular': NOTO_REGULAR,
  'Roboto Regular|bold': NOTO_BOLD,
  'Roboto Bold': NOTO_BOLD,
  'Roboto Bold|bold': NOTO_BOLD,
  Roboto: NOTO_REGULAR,
  'Roboto|bold': NOTO_BOLD,
  Comfortaa: NOTO_REGULAR,
  'Comfortaa|bold': NOTO_BOLD,
  Arial: NOTO_REGULAR,
  'Arial|bold': NOTO_BOLD,
  // The theme's own resolved families...
  'Comfortaa Regular': NOTO_REGULAR,
  'Comfortaa Regular|bold': NOTO_BOLD,
  'Comfortaa Bold': NOTO_BOLD,
  'Comfortaa Bold|bold': NOTO_BOLD,
  // ...and the fallback kendo reaches for when it can't resolve a family at
  // all. Inspecting an exported PDF showed the table and variance widgets
  // drawn in unembedded Helvetica, which is why the rupee sign came out as a
  // stray 0xB9 byte there while the KPI cards rendered it correctly.
  Helvetica: NOTO_REGULAR,
  'Helvetica|bold': NOTO_BOLD,
  'sans-serif': NOTO_REGULAR,
  'sans-serif|bold': NOTO_BOLD,
});

/* The preview needs the same face in the browser so it measures text in the
   font the PDF will actually be drawn in. Registered here rather than with an
   @font-face rule because css-loader tries to resolve a root-relative url() at
   build time, which fails for a file served out of public/. */
const registerPreviewFont = (weight: string, file: string) => {
  if (typeof FontFace === 'undefined') return;
  const face = new FontFace('Noto Sans', `url(${process.env.PUBLIC_URL}/fonts/${file})`, {
    weight,
  });
  face
    .load()
    .then((loaded) => document.fonts.add(loaded))
    .catch(() => {
      // Preview falls back to a system font; the embedded PDF font is separate.
    });
};

registerPreviewFont('400', 'NotoSans-Regular.ttf');
registerPreviewFont('700', 'NotoSans-Bold.ttf');

interface PreviewModalProperties {
  isOpen: boolean;
  onClose: () => void;
  selectedPages: any[];
}

const PreviewModal = ({ isOpen, onClose, selectedPages }: PreviewModalProperties) => {
  const dashboard = useSelector(getDashboard);
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const timeFilter = useSelector(getTimeFilter);
  const targetReference = useRef<any>();
  const [pdfExport, setPDFExport] = useState(false);
  const [allPageInstruments, setAllPageInstruments] = useState<PageInstruments>({});
  const [allInstrumentsData, setAllInstrumentsData] = useState<PageInstruments>({});
  const [isPageDataLoading, setIsPageDataLoading] = useState(false);
  const [isPageInstrumentsLoading, setIsPageInstrumentsLoading] = useState(false);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);
  const [pageSearch, setPageSearch] = useState('');
  const {
    isOpen: isExportPopoverOpen,
    onOpen: onExportPopoverOpen,
    onClose: onExportPopoverClose,
  } = useDisclosure();

  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const dashboardTimeFilter = useSelector(getTimeFilter);
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);
  const anchorPeriod = useSelector(getReportingAnchorPeriod);
  const reportingWindow = useSelector(getReportingWindow);
  const model = useSelector(getBlocks);
  const fyStartMonth: string =
    (model?.time_properties?.fy_start_month as string | undefined) ?? 'Jan';
  const contentLoading = isPageDataLoading || isPageInstrumentsLoading || pdfExport;
  const previewContentLoading = isPageDataLoading || isPageInstrumentsLoading;
  const pages = dashboard?.pages || [];

  const filteredPages = useMemo(
    () =>
      pages
        .slice()
        .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
        .filter((page: any) => page.name?.toLowerCase().includes(pageSearch.trim().toLowerCase())),
    [pages, pageSearch],
  );

  const selectedPagesForPreview = useMemo(
    () =>
      pages
        .slice()
        .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
        .filter((page: any) => selectedPageIds.includes(page.id)),
    [pages, selectedPageIds],
  );

  const hasSelectedPages = selectedPagesForPreview.length > 0;
  const allVisibleSelected =
    filteredPages.length > 0 &&
    filteredPages.every((page: any) => selectedPageIds.includes(page.id));
  const someVisibleSelected =
    filteredPages.some((page: any) => selectedPageIds.includes(page.id)) && !allVisibleSelected;
  useEffect(() => {
    if (isOpen) {
      setAllPageInstruments({});
      setAllInstrumentsData({});
      setIsPageDataLoading(false);
      setIsPageInstrumentsLoading(false);
      const initialSelectedIds =
        selectedPages?.map((page: any) => page.id).filter((id: string) => !!id) || [];

      if (initialSelectedIds.length > 0) {
        setSelectedPageIds(initialSelectedIds);
      } else if (pages?.length) {
        setSelectedPageIds(pages?.map((page: any) => page.id).filter((id: string) => !!id));
      } else {
        setSelectedPageIds([]);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      if (!pages?.length || !selectedDashboardScenario || !isOpen || !anchorPeriod) {
        setIsPageDataLoading(false);
        setIsPageInstrumentsLoading(false);
        return;
      }

      // Reset data first, then start loading
      setAllPageInstruments({});
      setAllInstrumentsData({});
      setIsPageDataLoading(true);
      setIsPageInstrumentsLoading(true);

      try {
        const compareScenarioId = compareDashboardScenario?.id || undefined;

        // Resolve the anchor + window to concrete start/end for the export call.
        // Pass fyStartMonth so YTD/FY calculations respect the model's fiscal year.
        const { start: reportingStart, end: reportingEnd } = resolveReportingPeriod(
          anchorPeriod,
          reportingWindow,
          fyStartMonth,
        );

        const response = await bloxBiService.dashboard_full_export_pdf(
          dashboard?.id,
          dashboardTimeFilter?.start ?? '',
          dashboardTimeFilter?.end ?? '',
          selectedDashboardScenario?.id,
          dashboard?.model_id,
          anchorPeriod,
          reportingWindow,
          reportingStart,
          reportingEnd,
          compareScenarioId,
        );
        const instrumentDataMap: any = {};
        const instrumentsMap: any = {};
        response.data.pages?.forEach((page: any) => {
          instrumentsMap[page.page_id] = page.metadata.instruments;
          instrumentDataMap[page.page_id] = page.data_values;
        });

        setAllPageInstruments(instrumentsMap);
        setIsPageDataLoading(false);

        setAllInstrumentsData(instrumentDataMap);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', error);
        // Reset to empty state on error
        setAllPageInstruments({});
        setAllInstrumentsData({});
      } finally {
        setIsPageDataLoading(false);
        setIsPageInstrumentsLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [
    selectedDashboardScenario,
    dashboardTimeFilter,
    anchorPeriod,
    reportingWindow,
    compareDashboardScenario,
    isOpen,
  ]);

  if (!dashboard || !selectedScenario || !timeFilter) {
    return null;
  }

  const onExport = () => {
    onExportPopoverClose();
    setPDFExport(true);

    try {
      if (targetReference.current) {
        setTimeout(() => {
          // The preview scrolls its tables the way the dashboard does, but
          // kendo doesn't clip a scroll viewport the same way the browser
          // paints it, so the PDF didn't match what was on screen. Clipping
          // the viewports for the duration of the draw pins the export to the
          // window the preview is showing — scroll offsets and all.
          // Read the offsets before clipping: turning a scrolled element's
          // overflow to hidden makes the browser reset its scrollLeft and
          // scrollTop, so reading them afterwards gave a window that didn't
          // match the preview.
          const previewRoot = document.querySelector<HTMLElement>('.a4-preview-wrapper');
          const restoreScroll = previewRoot ? pinScrollOffsets(previewRoot) : [];
          document.body.classList.add(EXPORT_CLIP_CLASS);
          try {
            targetReference.current.save();
          } finally {
            restoreScroll.forEach((restore) => restore());
            document.body.classList.remove(EXPORT_CLIP_CLASS);
          }
          setPDFExport(false);
          onClose();
        }, 1000);
      } else {
        setTimeout(() => {
          setPDFExport(false);
          onClose();
        }, 1000);
      }
    } catch {
      setTimeout(() => {
        setPDFExport(false);
        onClose();
      }, 1000);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent
        minW={'calc(210mm + 100px)'}
        maxW={'90vw'}
        borderRadius='14px'
        overflow='hidden'
        border='1px solid #D9D9D9'
      >
        <ModalHeader bg='white' borderBottom='1px solid #E8E9EB' p='16px'>
          <Flex width='100%' justifyContent='flex-end' alignItems='center' gap='11px'>
            <Popover
              placement='left-start'
              isOpen={isExportPopoverOpen}
              onOpen={onExportPopoverOpen}
              onClose={onExportPopoverClose}
              offset={[0, 12]}
            >
              <PopoverTrigger>
                <Button
                  h='32px'
                  px='14px'
                  bg='#8B5CF6'
                  borderRadius='9px'
                  color='white'
                  fontFamily='Roboto Regular'
                  fontWeight='500'
                  fontSize='13px'
                  width='fit-content'
                  _hover={{ bg: '#7C4DEF' }}
                  leftIcon={
                    contentLoading ? (
                      <ChakraSpinner size='sm' thickness='3px' />
                    ) : (
                      <FigmaFileDownloadIcon width={14} height={14} color='white' />
                    )
                  }
                  isLoading={contentLoading}
                  disabled={!(pages?.length && pages?.length > 0)}
                  loadingText='Content is being prepared'
                >
                  Export ({selectedPageIds.length}/
                  {pages?.length && pages?.length > 0 ? pages?.length : 0} pages)
                </Button>
              </PopoverTrigger>
              <PopoverContent
                maxW='300px'
                bg='white'
                border='1px solid #D9D9D9'
                p='14px'
                borderRadius='14px'
                boxShadow='0px 8px 24px rgba(18, 24, 38, 0.16)'
              >
                <PopoverBody p={0}>
                  <Text
                    fontFamily='Comfortaa'
                    fontWeight='bold'
                    fontSize='14px'
                    color='#121826'
                    mb='11px'
                  >
                    Export Pages
                  </Text>
                  <Input
                    placeholder='Search pages'
                    mb='9px'
                    bg='white'
                    border='1px solid #D9D9D9'
                    borderRadius='9px'
                    h='32px'
                    fontFamily='Roboto Regular'
                    fontSize='13px'
                    color='#121826'
                    _placeholder={{ color: '#BEBEBE' }}
                    _focus={{ borderColor: '#8B5CF6', boxShadow: 'none' }}
                    value={pageSearch}
                    onChange={(event) => setPageSearch(event.target.value)}
                  />
                  <Flex
                    align='center'
                    gap='7px'
                    mb='9px'
                    cursor='pointer'
                    onClick={() => {
                      if (allVisibleSelected) {
                        setSelectedPageIds([]);
                      } else {
                        const idsToAdd = pages
                          .slice()
                          .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
                          .map((page: any) => page.id)
                          .filter((id: string) => !!id);
                        setSelectedPageIds(idsToAdd);
                      }
                    }}
                  >
                    <Checkbox
                      size='sm'
                      colorScheme='purple'
                      isChecked={allVisibleSelected}
                      isIndeterminate={someVisibleSelected}
                      pointerEvents='none'
                    />
                    <Text fontFamily='Roboto Bold' fontWeight='700' fontSize='13px' color='#121826'>
                      Select all
                    </Text>
                  </Flex>
                  <Box
                    maxH='200px'
                    overflowY='auto'
                    className='scroll'
                    display='flex'
                    flexDirection='column'
                    gap='7px'
                  >
                    {filteredPages.map((page: any) => (
                      <Flex
                        key={page.id}
                        align='center'
                        gap='7px'
                        cursor='pointer'
                        onClick={() => {
                          setSelectedPageIds((previous) =>
                            previous.includes(page.id)
                              ? previous.filter((id) => id !== page.id)
                              : [...previous, page.id],
                          );
                        }}
                      >
                        <Checkbox
                          size='sm'
                          colorScheme='purple'
                          isChecked={selectedPageIds.includes(page.id)}
                          pointerEvents='none'
                        />
                        <Text
                          fontFamily='Roboto Regular'
                          fontSize='13px'
                          color='#121826'
                          noOfLines={1}
                        >
                          {page.name}
                        </Text>
                      </Flex>
                    ))}
                    {filteredPages.length === 0 && (
                      <Text fontFamily='Roboto Regular' fontSize='13px' color='#708090'>
                        No pages found.
                      </Text>
                    )}
                  </Box>
                </PopoverBody>
                <PopoverFooter
                  display='flex'
                  justifyContent='flex-end'
                  border='none'
                  p={0}
                  mt='11px'
                  gap='9px'
                >
                  <Button
                    h='30px'
                    px='12px'
                    bg='white'
                    border='1px solid #D9D9D9'
                    borderRadius='8px'
                    fontFamily='Roboto Regular'
                    fontWeight='500'
                    fontSize='12px'
                    color='#121826'
                    _hover={{ bg: 'gray.50' }}
                    onClick={onExportPopoverClose}
                  >
                    Cancel
                  </Button>

                  <Button
                    h='30px'
                    px='12px'
                    bg='#8B5CF6'
                    borderRadius='8px'
                    color='white'
                    fontFamily='Roboto Regular'
                    fontWeight='500'
                    fontSize='12px'
                    _hover={{ bg: '#7C4DEF' }}
                    leftIcon={<DownloadIcon />}
                    disabled={!hasSelectedPages}
                    onClick={() => {
                      setTimeout(() => onExport(), 300);
                    }}
                  >
                    Export
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
            <Button
              h='32px'
              px='14px'
              bg='white'
              border='1px solid #D9D9D9'
              borderRadius='9px'
              fontFamily='Roboto Regular'
              fontWeight='500'
              fontSize='13px'
              color='#121826'
              width='fit-content'
              _hover={{ bg: 'gray.50' }}
              onClick={onClose}
              leftIcon={contentLoading ? <ChakraSpinner size='sm' thickness='3px' /> : <></>}
              isLoading={contentLoading}
              disabled={contentLoading}
            >
              Cancel
            </Button>
          </Flex>
        </ModalHeader>
        <ModalBody
          className='pdf-preview-container scroll'
          maxH='75vh'
          overflowY='auto'
          overflowX='auto'
        >
          <Box>
            <PDFExport
              // A4 landscape is 841.89pt wide; less the 0.1cm margins that
              // leaves ~836pt for the 1440px wrapper, hence the 0.58 scale
              // below. 'auto' sized the page from the content instead, which
              // produced an unprintable 576x343pt sheet.
              paperSize='A4'
              margin='0.1cm'
              ref={targetReference}
              fileName={`${dashboard?.name} .pdf`}
              author='BLOX'
              title={`${dashboard?.name} Dashboard Export`}
              subject='Dashboard Export'
              creator='BLOX'
              date={new Date()}
              keepTogether='page'
              landscape
              scale={0.58}
              forcePageBreak='.page-break'
            >
              <Box className='a4-preview-wrapper'>
                {previewContentLoading && (
                  <>
                    <Text
                      color='black'
                      height={'80vh'}
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                    >
                      Loading preview...
                    </Text>
                  </>
                )}
                {!previewContentLoading && hasSelectedPages && (
                  <Preview
                    selectedPages={selectedPagesForPreview}
                    allPageInstruments={allPageInstruments}
                    allInstrumentsData={allInstrumentsData}
                    isPageDataLoading={isPageDataLoading}
                    isPageInstrumentsLoading={isPageInstrumentsLoading}
                  />
                )}
                {!previewContentLoading && !hasSelectedPages && (
                  <Text
                    color='black'
                    height={'80vh'}
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    Please select at least one page to export.
                  </Text>
                )}
              </Box>
            </PDFExport>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PreviewModal;
